import {
  AttendanceGateAlert,
  ConfirmModal,
  DateInput,
  NumberInput,
  ScholarshipSubStatusBadge,
  SelectInput,
  TextareaInput
} from '@app/components'
import {
  attendanceQueryKey,
  CAP_REQUEST_HELP_LABEL,
  CCCAP_HELP_LABEL,
  FAMILY_OUT_OF_POCKET_HELP_LABEL,
  formatCurrency,
  fundingPlaceholderProps,
  HEAD_START_HELP_LABEL,
  isAttendanceSaveRejected,
  isScholarshipLocked,
  OTHER_FUNDING_HELP_LABEL,
  payingZeroRequired,
  priorRequestedCap,
  PUBLISHED_TUITION_RATE_HELP_LABEL,
  scholarshipDisabledLabel,
  scholarshipLockedNote,
  SFP_HELP_LABEL,
  SFP_OPTIONS,
  showMessagesNotifications,
  UPK_HELP_LABEL,
  UPK_OPTIONS,
  useAttendance,
  useBlockNavigation,
  useFundingYearGate,
  useUpdateAttendance,
  YES_NO_OPTIONS
} from '@app/lib'
import {
  SugarAttendance,
  type SugarAttendanceEntry,
  type SugarAttendanceFundingWrite,
  type SugarAttendanceResponse,
  type SugarAttendanceUpsertEntry
} from '@app/types'
import {
  Alert,
  Anchor,
  Box,
  Button,
  Grid,
  Group,
  Input,
  LoadingOverlay,
  type MantineTheme,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip
} from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useShallowEffect } from '@mantine/hooks'
import { IconAlertCircle, IconChecks, IconInfoCircle, IconLock, IconLockOpen } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { filter, find, groupBy, isNumber, map, mapValues, some } from 'lodash'
import { type FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import * as yup from 'yup'
import { useSugarContext } from '@app/providers'

dayjs.extend(duration)

type FormData = {
  description: string
  numberOfDppKids: number
  numberOfKids: number
  entries: (Omit<
    SugarAttendanceEntry,
    | 'id'
    | 'earlyEndDate'
    | 'funding'
    | 'fundingPrefill'
    | 'lateStartDate'
    | 'isDppFamilyPayingZero'
    | 'rcvdOtherTuitionSupport'
    | 'upkParticipant'
  > & {
    ccapEnrolled: string | null
    earlyEndDate: Date | null
    earlyEndMax: Date | null
    earlyEndMin: Date | null
    enrollmentId: string | null
    familyOutOfPocket: number | ''
    fundingCollected: boolean
    gapScholarshipStatus: 'Yes' | 'No' | 'Pending' | null
    headStart: string | null
    headStartVisible: boolean
    isDppFamilyPayingZero: 'yes' | 'no' | null
    lateStartDate: Date | null
    lateStartMax: Date | null
    lateStartMin: Date | null
    maximumAllowedDays: number
    otherFunding: number | ''
    prefilledFields: string[]
    prefillSource: string | null
    publishedTuitionRate: number | ''
    rcvdOtherTuitionSupport: 'yes' | 'no' | null
    requestedCap: number | ''
    scholarshipLocked: boolean
    sfpEnrolled: string | null
    sfpVisible: boolean
    upkParticipant: 'yes' | 'no' | null
    upkType: string | null
  })[]
}

const formSchema = yup.object().shape({
  description: yup.string(),
  numberOfDppKids: yup.number().min(0, 'Must be a positive number'),
  numberOfKids: yup.number().min(0, 'Must be a positive number'),
  entries: yup.array().of(
    yup.object().shape(
      {
        ccapEnrolled: yup
          .string()
          .nullable()
          // A locked box cannot be filled, so requiring it would strand the
          // provider with no path forward. The roster's isFundingComplete makes the
          // same call. This leaves the UI looser than the server's rule, never
          // stricter — the safe direction.
          .when(['fundingCollected', 'scholarshipLocked'], ([fundingCollected, scholarshipLocked], schema) =>
            fundingCollected && !scholarshipLocked ? schema.required('Required') : schema
          ),
        daysAttended: yup
          .number()
          .required('Entry required')
          .min(0, 'Entry required')
          .when(['daysScheduled', 'maximumAllowedDays'], ([daysScheduled, maximumAllowedDays], schema) =>
            schema.max(Math.min(daysScheduled, maximumAllowedDays), 'Must be less than or equal to days scheduled')
          ),
        daysScheduled: yup
          .number()
          .required('Entry required')
          .min(0, 'Entry required')
          .when(
            ['maximumAllowedDays', 'lateStartDate', 'earlyEndDate', 'earlyEndMin', 'lateStartMax'],
            ([maximumAllowedDays, lateStartDate, earlyEndDate, earlyEndMin, lateStartMax], schema) => {
              let maxDays = maximumAllowedDays

              if (earlyEndDate && lateStartDate) {
                maxDays = Math.abs(dayjs(earlyEndDate).diff(lateStartDate, 'days')) + 1
              } else if (lateStartDate) {
                maxDays = Math.abs(dayjs(lateStartMax).diff(lateStartDate, 'days')) + 1
              } else if (earlyEndDate) {
                maxDays = Math.abs(dayjs(earlyEndMin).diff(earlyEndDate, 'days')) + 1
              }

              return schema.max(maxDays, `Maximum of ${maxDays} days`)
            }
          ),
        description: yup.string(),
        earlyEndDate: yup
          .date()
          .nullable()
          .when(['lateStartDate', 'earlyEndMin', 'earlyEndMax'], ([lateStartDate, earlyEndMin, earlyEndMax], schema) =>
            lateStartDate
              ? schema
                  .min(dayjs(lateStartDate).add(1, 'day').toDate(), 'Must be after start date')
                  .max(earlyEndMax, 'Max date allowed is ' + earlyEndMax.toLocaleDateString())
              : schema
                  .min(earlyEndMin, 'Must be on or after ' + earlyEndMin.toLocaleDateString())
                  .max(earlyEndMax, 'Max date allowed is ' + earlyEndMax.toLocaleDateString())
          ),
        earlyEndMax: yup.date().nullable(),
        earlyEndMin: yup.date().nullable(),
        enrollmentId: yup.string().nullable(),
        familyOutOfPocket: yup
          .number()
          // The form holds '' for an empty NumberInput; yup would coerce that to
          // NaN, so map it to null and let `required` speak.
          .transform((value, original) => (original === '' ? null : value))
          .nullable()
          .min(0, 'Must be a non-negative number')
          .when(['fundingCollected'], ([fundingCollected], schema) =>
            fundingCollected ? schema.required('Required') : schema
          ),
        fundingCollected: yup.boolean(),
        gapScholarshipStatus: yup.string().oneOf(['Yes', 'No', 'Pending']).nullable(),
        gapStatus: yup.string().nullable(),
        headStart: yup
          .string()
          .nullable()
          .when(
            ['fundingCollected', 'headStartVisible', 'scholarshipLocked'],
            ([fundingCollected, headStartVisible, scholarshipLocked], schema) =>
              fundingCollected && headStartVisible && !scholarshipLocked ? schema.required('Required') : schema
          ),
        headStartVisible: yup.boolean(),
        isDppFamilyPayingZero: yup
          .string()
          .oneOf(['yes', 'no'])
          .nullable()
          // Required of an active scholarship only (MIQ-2954). That is the rule a
          // pre-2026/27 sheet always had -- tri-state 'Yes' is raw 'active' and
          // nothing else -- so no year guard is needed. gapStatus is not seeded onto
          // the row; validate() merges it from the live enrollment, so a status that
          // changes under an open sheet moves this rule with it.
          .when(['gapStatus'], ([gapStatus], schema) =>
            payingZeroRequired(gapStatus) ? schema.required('Required') : schema
          )
          // MIQ-2922's server rule, mirrored so Submit is blocked before the
          // request: the two answers must agree. Scoped exactly as the server
          // scopes it -- the rows that owe a Paying $0 answer, active only since
          // MIQ-2953 -- and only once both answers are in, since a blank on either
          // side is `required`'s to report. A consistency check only: Family Out of
          // Pocket above $0 is a legitimate answer on a scholarship row the award
          // does not fully cover.
          .test('agrees-with-family-out-of-pocket', function (payingZero) {
            const { familyOutOfPocket, fundingCollected, gapStatus } = this.parent

            if (!fundingCollected || !payingZeroRequired(gapStatus) || !isNumber(familyOutOfPocket) || !payingZero) {
              return true
            }

            if (payingZero === 'yes' && familyOutOfPocket > 0) {
              return this.createError({ message: 'Cannot be Yes when Family Out of Pocket is above $0' })
            }

            if (payingZero === 'no' && familyOutOfPocket === 0) {
              return this.createError({ message: 'Cannot be No when Family Out of Pocket is $0' })
            }

            return true
          }),
        lateStartDate: yup
          .date()
          .nullable()
          .when(
            ['earlyEndDate', 'lateStartMax', 'lateStartMin'],
            ([earlyEndDate, lateStartMax, lateStartMin], schema) =>
              earlyEndDate
                ? schema
                    .min(lateStartMin, 'Must be on or after ' + lateStartMin.toLocaleDateString())
                    .max(dayjs(earlyEndDate).subtract(1, 'day').toDate(), 'Must be before end date')
                : schema
                    .min(lateStartMin, 'Must be on or after ' + lateStartMin.toLocaleDateString())
                    .max(lateStartMax, 'Max date allowed is ' + lateStartMax.toLocaleDateString())
          ),
        lateStartMax: yup.date().nullable(),
        lateStartMin: yup.date().nullable(),
        maximumAllowedDays: yup.number(),
        otherFunding: yup
          .number()
          // The form holds '' for an empty NumberInput; yup would coerce that to
          // NaN, so map it to null and let `required` speak.
          .transform((value, original) => (original === '' ? null : value))
          .nullable()
          .min(0, 'Must be a non-negative number')
          .when(['fundingCollected'], ([fundingCollected], schema) =>
            fundingCollected ? schema.required('Required') : schema
          ),
        prefilledFields: yup.array().of(yup.string()),
        prefillSource: yup.string().nullable(),
        publishedTuitionRate: yup
          .number()
          // The form holds '' for an empty NumberInput; yup would coerce that to
          // NaN, so map it to null and let `required` speak.
          .transform((value, original) => (original === '' ? null : value))
          .nullable()
          .min(0, 'Must be a non-negative number')
          // MIQ-2922: $0 is not a rate, unlike Other Funding and Family Out of
          // Pocket, where 0 is an answer. The server rejects it per item; this
          // blocks Submit first, in the program lead's wording. Scoped to sheets
          // that collect funding, like every other rule here: a pre-2026/27 sheet
          // renders no funding block at all, so a stored 0 on one would leave the
          // form invalid with no control the provider could use to fix it. The
          // non-negative rule above stays for both, so a negative value keeps its
          // own message rather than being reported as a zero.
          .when(['fundingCollected'], ([fundingCollected], schema) =>
            fundingCollected
              ? schema
                  // A negative keeps the non-negative wording: both rules fail on
                  // one, and yup reports this one first.
                  .moreThan(0, ({ value }) =>
                    Number(value) < 0 ? 'Must be a non-negative number' : 'Published tuition cannot be $0'
                  )
                  .required('Required')
              : schema
          ),
        rcvdOtherTuitionSupport: yup
          .string()
          .oneOf(['yes', 'no'])
          .nullable()
          // Mirrors upkParticipant below. Both controls render only where the
          // funding block does not (pre-2026/2027, or a backend without the
          // funding contract), because otherFunding and upkType replace them
          // wherever it does -- so the rule that has always required them on a
          // scholarship row has to be scoped the same way. Dropping it outright
          // would let those sheets submit blank, and nothing server-side
          // enforces these two.
          .when(['fundingCollected', 'gapScholarshipStatus'], ([fundingCollected, gapScholarshipStatus], schema) =>
            !fundingCollected && gapScholarshipStatus === 'Yes' ? schema.required('Required') : schema
          ),
        requestedCap: yup
          .number()
          .transform((value, original) => (original === '' ? null : value))
          .nullable()
          .min(0, 'Must be a non-negative number'),
        scholarshipLocked: yup.boolean(),
        sfpEnrolled: yup
          .string()
          .nullable()
          .when(['fundingCollected', 'sfpVisible'], ([fundingCollected, sfpVisible], schema) =>
            fundingCollected && sfpVisible ? schema.required('Required') : schema
          ),
        sfpVisible: yup.boolean(),
        upkParticipant: yup
          .string()
          .oneOf(['yes', 'no'])
          .nullable()
          // See rcvdOtherTuitionSupport above -- same control lifetime, same rule.
          .when(['fundingCollected', 'gapScholarshipStatus'], ([fundingCollected, gapScholarshipStatus], schema) =>
            !fundingCollected && gapScholarshipStatus === 'Yes' ? schema.required('Required') : schema
          ),
        upkType: yup
          .string()
          .nullable()
          .when(['fundingCollected'], ([fundingCollected], schema) =>
            fundingCollected ? schema.required('Required') : schema
          )
      },
      [
        ['daysAttended', 'daysScheduled'],
        ['daysScheduled', 'lateStartDate'],
        ['daysScheduled', 'earlyEndDate'],
        ['lateStartDate', 'earlyEndDate'],
        ['earlyEndDate', 'lateStartDate']
      ]
    )
  )
})

// MIQ-2850: the attendance response carries tuitionCredit, tuitionCap and
// dppTuitionCap as Sugar currency strings ("950.000000") or null, deliberately
// uncoerced so an unset cap stays distinguishable from a real cap of zero.
// Number(null) and Number('') are both 0, so one falsy check collapses unset,
// cleared and zero into no value -- a cap of zero is not a cap, and rendering
// it as "$0.00" would read as one. formatCurrency(null) renders an em dash.
const currencyOrDash = (value: string | number | null | undefined) => formatCurrency(Number(value) || null)

const formValidate = yupResolver(formSchema)

// The funding keys the server may prefill (PR #734). Days Scheduled and Days
// Attended are excluded by the contract and filtered here regardless — a day
// count that looks carried forward is worse than one that looks empty.
const FUNDING_PREFILL_FIELDS = [
  'ccapEnrolled',
  'headStart',
  'upkType',
  'sfpEnrolled',
  'otherFunding',
  'familyOutOfPocket',
  'publishedTuitionRate'
]

// Mantine 6's Tooltip sets no aria attributes at all, so the help text would never
// reach a screen reader on its own; the visually-hidden span carries it and
// aria-describedby points at it. Same approach as the roster's FundingColumnHeader,
// but for a form label rather than a table header, so none of that component's
// nowrap/portal handling applies.
const fundingLabel = (label: string, help: string, optional = false) => (
  <Group
    noWrap
    spacing={4}
  >
    <Text
      fw={500}
      size='sm'
    >
      {label}
      {optional && (
        <Text
          color='dimmed'
          component='span'
          fw={400}
          size='sm'
        >
          {' '}
          (optional)
        </Text>
      )}
    </Text>
    <Tooltip
      events={{ focus: false, hover: true, touch: true }}
      label={help}
      multiline
      width={320}
      withArrow
      withinPortal
    >
      {/* Decorative and deliberately not focusable. Mantine renders this whole
          helper inside the field's own <label>, and a focusable button nested in
          a label is invalid markup that also lets a click on the icon activate
          the field. Keyboard and screen-reader users reach the same text through
          aria-describedby on the control itself, so nothing is lost by making the
          icon mouse-only. */}
      <Box
        aria-hidden
        c='gray.6'
        component='span'
        sx={{ display: 'inline-flex' }}
      >
        <IconInfoCircle size={16} />
      </Box>
    </Tooltip>
  </Group>
)

// The prior period arrives named by its key, "2026-09". V3's hint reads
// "Pre-filled from September 2026", and the period picker on this same sheet
// formats it exactly this way. A name in any other shape is the server's to
// phrase and passes through untouched.
const prefillPeriodName = (name: string | null) =>
  name && /^\d{4}-\d{2}$/.test(name) ? dayjs(name, 'YYYY-MM').format('MMMM YYYY') : name

// V3 puts the "Pre-filled from" hint under the box. Mantine 6's default order
// renders `description` between the label and the input instead, which pushed a
// hinted box below its neighbours on the row.
const BELOW_CONTROL: ('label' | 'input' | 'description' | 'error')[] = ['label', 'input', 'description', 'error']

// V3's pre-filled highlight, in theme colours rather than the mockup's hex. The
// sheet-level legend's swatch is drawn from the same object, so the key and the
// boxes it describes cannot drift apart.
const prefilledBox = (theme: MantineTheme) => ({
  backgroundColor: theme.colors.yellow[0],
  borderColor: theme.colors.yellow[3]
})

// '' is what an empty Mantine NumberInput holds; it must become null and never 0.
// 0 is a real answer here — the Family Out of Pocket copy asks the provider to
// enter $0 explicitly — so the two must not collide.
const toCurrency = (value: number | ''): number | null => (value === '' ? null : value)

// Head Start and SFP are omitted rather than sent as null when hidden: the server
// rejects a write to a stream the site does not have for the period, and that
// rejection would take the whole row's day counts with it. Same reason
// toUpsertItem omits them on the roster. capRequested, headStartVisible and
// sfpVisible are read-only and never sent.
const toFundingWrite = (
  row: FormData['entries'][number],
  visible: { headStart: boolean; sfp: boolean }
): SugarAttendanceFundingWrite => {
  const funding: SugarAttendanceFundingWrite = {
    ccapEnrolled: row.ccapEnrolled,
    familyOutOfPocket: toCurrency(row.familyOutOfPocket),
    otherFunding: toCurrency(row.otherFunding),
    publishedTuitionRate: toCurrency(row.publishedTuitionRate),
    requestedCap: toCurrency(row.requestedCap),
    upkType: row.upkType
  }

  // Visibility comes from the live sheet, not the row: a stream that went hidden
  // since the form was seeded must not be posted (the server rejects the write
  // and takes the row's day counts with it), and one that became visible must
  // not be silently dropped from the payload.
  if (visible.headStart) funding.headStart = row.headStart
  if (visible.sfp) funding.sfpEnrolled = row.sfpEnrolled

  return funding
}

export const AttendanceTable: FC = () => {
  const {
    attendancePeriodId: periodId,
    attendancePeriods: periods,
    attendanceProgramYearId: programYearId,
    attendanceProgramYears: programYears,
    isSelectionRestored,
    options,
    setAttendancePeriodId: setPeriodId,
    setAttendanceProgramYearId: setProgramYearId,
    setHasUnsavedChanges,
    siteId,
    sites
  } = useSugarContext()
  const {
    data: attendance,
    isError: isAttendanceError,
    isFetching: isFetchingAttendance,
    isPlaceholderData
  } = useAttendance()
  const { updateAttendance, isUpdatingAttendance } = useUpdateAttendance()
  const queryClient = useQueryClient()
  // The live selection, readable from a save callback that captured an older one.
  // Assigned during render rather than in an effect so it is already current when a
  // save started before the switch resolves after it.
  const selectionRef = useRef({ periodId, programYearId, siteId })

  selectionRef.current = { periodId, programYearId, siteId }
  const isFundingYearGated = useFundingYearGate(programYearId, programYears)
  const idPrefix = useId()
  // Keyed on enrollmentId: the server rejects per item, and that is the only id it
  // sends back on the message.
  const [rowErrors, setRowErrors] = useState<Record<string, string[]>>({})
  const helpIds = useMemo(
    () => ({
      cap: `${idPrefix}cap`,
      ccap: `${idPrefix}ccap`,
      familyOutOfPocket: `${idPrefix}family-out-of-pocket`,
      headStart: `${idPrefix}head-start`,
      otherFunding: `${idPrefix}other-funding`,
      publishedTuitionRate: `${idPrefix}published-tuition-rate`,
      sfp: `${idPrefix}sfp`,
      upk: `${idPrefix}upk`
    }),
    [idPrefix]
  )
  // The four currency fields share every prop but field name, label, help copy and
  // (Cap Request only) the optional/not-required treatment — collapsed to one map
  // rather than four hand-written NumberInput blocks.
  const currencyFundingFields: {
    field: 'otherFunding' | 'requestedCap' | 'familyOutOfPocket' | 'publishedTuitionRate'
    help: string
    helpId: string
    label: string
    optional?: boolean
  }[] = [
    { field: 'otherFunding', help: OTHER_FUNDING_HELP_LABEL, helpId: helpIds.otherFunding, label: 'Other Funding' },
    { field: 'requestedCap', help: CAP_REQUEST_HELP_LABEL, helpId: helpIds.cap, label: 'Cap Request', optional: true },
    {
      field: 'familyOutOfPocket',
      help: FAMILY_OUT_OF_POCKET_HELP_LABEL,
      helpId: helpIds.familyOutOfPocket,
      label: 'Family Out of Pocket'
    },
    {
      field: 'publishedTuitionRate',
      help: PUBLISHED_TUITION_RATE_HELP_LABEL,
      helpId: helpIds.publishedTuitionRate,
      label: 'Published Tuition Rate'
    }
  ]
  const getInitialValues = useCallback(
    (attendance?: SugarAttendance | null | undefined, isYearGated = false): FormData => ({
      description: attendance?.sheet.description ?? '',
      entries: map(attendance?.entries, ({ enrollment, entry, rules }) => ({
        daysAttended: entry?.daysAttended ?? -1,
        daysScheduled: entry?.daysScheduled ?? -1,
        description: entry?.description ?? '',
        earlyEndDate: entry?.earlyEndDate ? dayjs(entry.earlyEndDate).toDate() : null,
        earlyEndMax: rules?.earlyEndMax ? dayjs(rules.earlyEndMax).toDate() : null,
        earlyEndMin: rules?.earlyEndMin ? dayjs(rules.earlyEndMin).toDate() : null,
        enrollmentId: enrollment?.id ?? null,
        gapScholarshipStatus: enrollment?.gapScholarshipStatus ?? null,
        isDppFamilyPayingZero: entry?.isDppFamilyPayingZero ?? null,
        lateStartDate: entry?.lateStartDate ? dayjs(entry.lateStartDate).toDate() : null,
        lateStartMax: rules?.lateStartMax ? dayjs(rules.lateStartMax).toDate() : null,
        lateStartMin: rules?.lateStartMin ? dayjs(rules.lateStartMin).toDate() : null,
        maximumAllowedDays: rules?.maximumAllowedDays ?? 0,
        rcvdOtherTuitionSupport: entry?.rcvdOtherTuitionSupport ?? null,
        upkParticipant: entry?.upkParticipant ?? null,
        // A funding object absent from the GET means a backend without PR #730:
        // render nothing and post nothing, exactly as this page behaves today.
        fundingCollected: Boolean(entry?.funding) && !isYearGated,
        ccapEnrolled: entry?.funding?.ccapEnrolled ?? null,
        headStart: entry?.funding?.headStart ?? null,
        upkType: entry?.funding?.upkType ?? null,
        sfpEnrolled: entry?.funding?.sfpEnrolled ?? null,
        otherFunding: entry?.funding?.otherFunding ?? '',
        familyOutOfPocket: entry?.funding?.familyOutOfPocket ?? '',
        publishedTuitionRate: entry?.funding?.publishedTuitionRate ?? '',
        requestedCap: entry?.funding?.requestedCap ?? '',
        headStartVisible: entry?.funding?.headStartVisible ?? false,
        sfpVisible: entry?.funding?.sfpVisible ?? false,
        // Display only: PR #734's prefill persists nothing, so these values are
        // lost unless the provider submits. Filtered to funding keys because
        // Days Scheduled / Attended must never show a carry-forward hint.
        prefilledFields: filter(entry?.fundingPrefill?.fields ?? [], (field) => FUNDING_PREFILL_FIELDS.includes(field)),
        prefillSource:
          entry?.fundingPrefill?.source === 'roster'
            ? 'your roster'
            : prefillPeriodName(entry?.fundingPrefill?.sourcePeriodName ?? null),
        // Seeded for the first paint only. The lock is server truth and can change
        // under a sheet the provider is mid-edit, so every later read comes from
        // the live enrollment -- see lockedByEnrollmentId below.
        scholarshipLocked: isScholarshipLocked(enrollment?.gapStatus ?? null)
      })),
      numberOfDppKids: attendance?.sheet.numberOfDppKids || 0,
      numberOfKids: attendance?.sheet.numberOfKids || 0
    }),
    []
  )
  // Every row flag the SERVER owns, in one place. None of these belong in form
  // state: form state holds what the provider typed, and a copy of server truth
  // taken when the form was seeded goes stale the moment Sugar changes
  // underneath it. This sheet's reseed keys on the gate, period and site -- not
  // on scholarship status or the per-period stream toggles -- so a same-period
  // refetch leaves any cached copy untouched while the rendered badge, columns
  // and rules move on. The roster learned this on MIQ-2702 and derives per row;
  // isScholarshipLocked's own comment records why.
  //
  // Keyed by enrollmentId rather than by index so it cannot mis-pair if a
  // period's rows ever come back in a different order.
  // Identity of the row list, not its contents: which students are on the sheet
  // and in what order. Drives the reseed below.
  // Which students are on the sheet, in what order, and whether the response
  // carries funding for each. Funding presence belongs here because its ABSENCE
  // is the pre-#730 compatibility signal: a sheet first read from a backend
  // without the funding contract, refetched mid-period once that backend has it,
  // keeps the same ids and would otherwise hold fundingCollected false until the
  // provider changed period or site -- no funding block, and no funding object
  // posted, on a sheet the server now requires one for.
  const entryShapeKey = useMemo(
    () =>
      map(
        attendance?.data.entries,
        ({ enrollment, entry }) => `${enrollment?.id ?? ''}:${entry?.funding ? 'f' : ''}`
      ).join('|'),
    [attendance?.data.entries]
  )
  const liveMetaByEnrollmentId = useMemo(
    () =>
      new Map(
        map(attendance?.data.entries, ({ enrollment, entry }) => [
          enrollment?.id ?? '',
          {
            gapScholarshipStatus: enrollment?.gapScholarshipStatus ?? null,
            gapStatus: enrollment?.gapStatus ?? null,
            headStartVisible: entry?.funding?.headStartVisible ?? false,
            scholarshipLocked: isScholarshipLocked(enrollment?.gapStatus ?? null),
            sfpVisible: entry?.funding?.sfpVisible ?? false
          }
        ])
      ),
    [attendance?.data.entries]
  )
  // Validation has to agree with what the controls render, or a required rule
  // asks for a field that is locked, or hidden, or no longer applies. yup reads
  // these as siblings of the row, so the live values are merged in here rather
  // than written into form state -- writing them would mark the form dirty on a
  // server-side change the provider never made, arming the unsaved-changes guard
  // and the Submit button behind their back.
  const validate = useCallback(
    (values: FormData) =>
      formValidate({
        ...values,
        entries: map(values.entries, (row) => ({ ...row, ...liveMetaByEnrollmentId.get(row.enrollmentId ?? '') }))
      }),
    [liveMetaByEnrollmentId]
  )
  const form = useForm<FormData>({
    initialValues: getInitialValues(attendance?.data, isFundingYearGated),
    validate,
    validateInputOnChange: true
  })
  const sheet = attendance?.data.sheet
  const isGated = sheet
    ? (sheet.attendanceGated ?? find(sites, ['id', siteId])?.publishedTuitionRatesComplete !== true)
    : false
  // Three ways the sheet backing this form can fail to describe the selected period
  // as the server currently sees it, and all of them fail closed. Before the
  // provider's post-mount effect restores the stored selection, this is keyed to the
  // SSR default (newest program year, last period). Under keepPreviousData during a
  // switch, it is the previous selection's sheet. And when a fetch has exhausted its
  // retries React Query keeps the last good response, so after the server refuses a
  // save the cache can still hold the ungated sheet the save was attempted from —
  // re-enabling Submit on a period the server just rejected. Lock until a sheet we
  // trust lands rather than leave inputs and Submit live.
  const isLocked = isAttendanceError || !isSelectionRestored || isPlaceholderData || (sheet?.locked ?? true) || isGated
  const showGateAlert = isGated && isSelectionRestored && !isPlaceholderData
  const variant = isLocked ? 'unstyled' : 'default'
  const isDirty = form.isDirty()
  const isValid = form.isValid()
  const isBusy = isUpdatingAttendance || isFetchingAttendance

  // Unsaved edits and an in-flight save only. Blocking on a background refetch too
  // popped "You have unsaved changes" on a clean form — now reachable from the gate
  // banner's own Go to Tuition Rates link, which is the one route out of a gated
  // period and fires exactly while that refetch is running.
  const { cancelNavigation, confirmNavigation, isBlockingNavigation } = useBlockNavigation(
    isDirty || isUpdatingAttendance
  )
  const numberInputFormatter = useCallback((val: string) => (String(val) === '-1' ? '' : val), [])
  // Not memoized: it reads form.values on every call, and form.values is a plain
  // useState snapshot in Mantine 6, not a stable accessor — a useCallback(fn, [])
  // here would permanently capture the mount-time array.
  const clearPrefill = (entryIndex: number, field: string) => {
    const current = form.values.entries[entryIndex]?.prefilledFields ?? []

    if (current.includes(field)) {
      form.setFieldValue(
        `entries.${entryIndex}.prefilledFields`,
        current.filter((prefilled) => prefilled !== field)
      )
    }
  }
  // Highlight and hint arrive together and leave together, the moment the provider
  // edits the field (clearPrefill): the value is theirs from then on. A read-only
  // sheet keeps the hint but drops the highlight, which asks for a review nobody
  // can act on there. Spread before fundingPlaceholderProps so an empty box that
  // owes an answer still gets the red "Required" styling.
  const prefillProps = (row: FormData['entries'][number] | undefined, field: string) =>
    row?.prefilledFields.includes(field) && row.prefillSource
      ? {
          description: `Pre-filled from ${row.prefillSource}`,
          ...(isLocked ? {} : { styles: (theme: MantineTheme) => ({ input: prefilledBox(theme) }) })
        }
      : {}
  // The four select funding controls (CCCAP, Head Start, UPK Type, SFP) share this
  // exact onChange body apart from the field name; the currency NumberInputs keep
  // their own inline handler since their value type is number | '', not string | null.
  const clearAndSet = (entryIndex: number, field: string) => (value: string | null) => {
    clearPrefill(entryIndex, field)
    form.setFieldValue(`entries.${entryIndex}.${field}`, value)
  }
  const resetForm = useCallback(
    (data: SugarAttendance | null | undefined) => {
      const values = getInitialValues(data, isFundingYearGated)

      form.setValues(values)
      form.resetDirty(values)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFundingYearGated]
  )

  // The gate fields are dependencies because a gate change is the one same-period
  // change that replaces the sheet's contents wholesale. A gate_unavailable envelope
  // can arrive with entries: [], and staleTime 0 retries it on focus or remount; the
  // retry reuses this query key, so without these the rows would render from the
  // fresh payload while the inputs stayed bound to the empty envelope. gateReason is
  // here as well as attendanceGated because the two states that matter most differ
  // only in the reason: gate_unavailable (entries possibly empty) retrying into
  // tuition_rates_incomplete (entries populated) leaves attendanceGated true both
  // times. Losing the gate discards edits on a period that just became read-only,
  // which is the same call the refused-save path makes.
  //
  // ponytail: enumerating the fields that can change a payload's shape, rather than
  // reseeding on any payload the user has not edited. A `if (!form.isDirty())` mirror
  // keyed on the whole response would not need extending for the next state, but it
  // changes when every reset fires on a form holding a month of attendance — worth
  // doing deliberately, not inside a review cycle.
  useEffect(() => {
    resetForm(attendance?.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attendance?.data.sheet.attendanceGated,
    attendance?.data.sheet.gateReason,
    attendance?.data.sheet.periodId,
    attendance?.data.sheet.siteId,
    // The rendered rows come from the live response while their values are read
    // out of form state by position, so the two lists have to describe the same
    // students in the same order. A same-period refetch that adds, removes or
    // reorders an enrollment would otherwise pair a student with the previous
    // occupant of their slot -- values, enrollmentId and all. Reseeding on the
    // shape is what the roster does for the same reason; it costs unsaved edits
    // only when the sheet's shape actually changes, which is rare and is the one
    // case where keeping them would attribute them to the wrong child.
    entryShapeKey,
    isFundingYearGated,
    resetForm
  ])

  // Deliberately NOT folded into the reseed above. A rejection belongs to the
  // sheet it came from: carried into another month or site it would sit on a row
  // that never failed, so a selection or gate change clears it. A shape change
  // must not, because the submit that produced those rejections is itself what
  // triggers the refetch -- clearing here would erase the inline errors (and the
  // replayed values) on exactly the reorder the shape key exists to handle.
  useEffect(() => {
    setRowErrors({})
  }, [
    attendance?.data.sheet.attendanceGated,
    attendance?.data.sheet.gateReason,
    attendance?.data.sheet.periodId,
    attendance?.data.sheet.siteId
  ])

  // Published so the header's site picker prompts before switching: that switch
  // never touches the router, so useBlockNavigation above cannot see it, and the
  // effect right above would silently reset a month of entries. No dependency
  // array on purpose — the flag lives in a provider a second screen can also
  // write, so re-asserting it every render is what keeps the two in step. React
  // bails out when the value is unchanged, so it costs nothing.
  useEffect(() => {
    setHasUnsavedChanges(isDirty)
  })

  useEffect(() => () => setHasUnsavedChanges(false), [setHasUnsavedChanges])

  useShallowEffect(() => {
    if (!isLocked) {
      // manually calling validate, since it doesn't appear to reliably fire
      form.validate()
    }
    // liveMetaByEnrollmentId is a dependency because the rules themselves change
    // with it, not just the values: a same-period refetch that locks a row, hides
    // a stream or moves a scholarship status changes what is required without
    // touching form.values or isLocked. Without it, form.errors and isValid()
    // would keep answering under the previous rule -- a newly unlocked blank row
    // could still submit, a newly locked one could stay blocked.
  }, [form.values, isLocked, liveMetaByEnrollmentId])

  return (
    <Stack spacing='lg'>
      <Box
        sx={{
          border: 0,
          clip: 'rect(0, 0, 0, 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1
        }}
      >
        <span id={helpIds.ccap}>{CCCAP_HELP_LABEL}</span>
        <span id={helpIds.headStart}>{HEAD_START_HELP_LABEL}</span>
        <span id={helpIds.upk}>{UPK_HELP_LABEL}</span>
        <span id={helpIds.sfp}>{SFP_HELP_LABEL}</span>
        <span id={helpIds.otherFunding}>{OTHER_FUNDING_HELP_LABEL}</span>
        <span id={helpIds.cap}>{CAP_REQUEST_HELP_LABEL}</span>
        <span id={helpIds.familyOutOfPocket}>{FAMILY_OUT_OF_POCKET_HELP_LABEL}</span>
        <span id={helpIds.publishedTuitionRate}>{PUBLISHED_TUITION_RATE_HELP_LABEL}</span>
      </Box>
      {showGateAlert && (
        <AttendanceGateAlert
          message={sheet?.gateMessage ?? null}
          reason={sheet?.attendanceGated === undefined ? 'tuition_rates_incomplete' : (sheet?.gateReason ?? null)}
        />
      )}
      <Paper
        p='lg'
        pos='relative'
      >
        <LoadingOverlay
          visible={isBusy}
          radius='lg'
        />
        <Stack spacing='xs'>
          <Group
            align='center'
            spacing='xs'
          >
            <Title
              fw={700}
              order={4}
            >
              {/* A gate_unavailable sheet arrives with a null name, so keying the
                  fallback on the name alone would caption a finished fetch
                  "Loading..." while the alert underneath explains the lock. No sheet
                  at all is the only genuine loading state. */}
              {sheet ? (sheet.name ?? find(periods, ['id', periodId])?.name ?? 'Attendance') : 'Loading...'}
            </Title>
            {isLocked ? (
              <IconLock
                color='red'
                size={18}
              />
            ) : (
              <IconLockOpen
                color='green'
                size={18}
              />
            )}
          </Group>
          <Group
            align='top'
            grow
            position='center'
            spacing='xs'
          >
            <SelectInput
              data={useMemo(
                () =>
                  map(programYears, (programYear) => ({
                    label: programYear.name,
                    value: programYear.id
                  })),
                [programYears]
              )}
              // Disabled while busy, like every other control on this sheet. A save
              // captures the selection it was launched from, so switching mid-flight
              // would land the old period's values under the new period's header once
              // the save resolved — the reset effect keys on the sheet's own period and
              // would not fire again to correct it.
              disabled={isBusy}
              label='Program Year'
              mb={0}
              onChange={setProgramYearId}
              value={programYearId}
            />
            <SelectInput
              data={useMemo(
                () =>
                  map(periods, (period) => ({
                    label: dayjs(period.name, 'YYYY-MM').format('MMMM YYYY'),
                    value: period.id
                  })),
                [periods]
              )}
              disabled={isBusy}
              label='Period'
              mb={0}
              onChange={setPeriodId}
              value={periodId}
            />
          </Group>
          <Group
            align='top'
            grow
            position='center'
            spacing='xs'
          >
            <NumberInput
              disabled={isBusy}
              label='# of students in DPP classroom'
              mb={0}
              readOnly={isLocked}
              variant={variant}
              {...form.getInputProps('numberOfKids')}
            />
            <NumberInput
              disabled={isBusy}
              label='# of DPP eligible students'
              mb={0}
              readOnly={isLocked}
              variant={variant}
              {...form.getInputProps('numberOfDppKids')}
            />
          </Group>
          <TextareaInput
            autosize
            disabled={isBusy}
            label='Notes'
            maxRows={3}
            mb={0}
            minRows={1}
            readOnly={isLocked}
            variant={variant}
            {...form.getInputProps('description')}
          />
        </Stack>
      </Paper>
      {attendance?.data.entries.length ? (
        <>
          <Title
            color='white'
            order={3}
          >
            Students
          </Title>
          {/* V3's sheet-level key to the highlight. Only where the funding block
              renders and the sheet can still be submitted: nothing is highlighted
              anywhere else, and a read-only sheet has no submit to review for. */}
          {!isLocked && some(form.values.entries, 'fundingCollected') && (
            <Stack spacing={4}>
              <Group
                noWrap
                spacing='xs'
              >
                <Box
                  aria-hidden
                  h={12}
                  miw={12}
                  sx={(theme) => ({ border: '1px solid', borderRadius: 3, ...prefilledBox(theme) })}
                  w={12}
                />
                <Text
                  color='white'
                  size='sm'
                >
                  Highlighted fields are pre-filled from the prior month. Review before submitting.
                </Text>
              </Group>
              {/* MIQ-2923: which fields the next sheet will pre-fill -- the labels of
                  FUNDING_PREFILL_FIELDS -- and which it never does. Head Start and SFP
                  are named only on a sheet that shows them. */}
              <Text
                color='white'
                size='sm'
              >
                Carry over from month to month:{' '}
                {[
                  'CCCAP',
                  ...(some(attendance?.data.entries, 'entry.funding.headStartVisible') ? ['Head Start'] : []),
                  'UPK Type',
                  ...(some(attendance?.data.entries, 'entry.funding.sfpVisible') ? ['SFP'] : []),
                  'Other Funding',
                  'Family Out of Pocket',
                  'Published Tuition Rate'
                ].join(', ')}
                . Never carried over: Days Scheduled, Days Attended, Cap Request.
              </Text>
            </Stack>
          )}
          {map(attendance?.data.entries, (attendanceItem, attendanceItemIdx) => {
            const row = form.values.entries[attendanceItemIdx]
            // Live, not the row copies: see liveMetaByEnrollmentId. The row's
            // copies are a first-paint seed, and the validator is handed these
            // same live values, so the controls and the rules cannot disagree.
            const scholarshipLocked = isScholarshipLocked(attendanceItem.enrollment.gapStatus)
            const headStartVisible = attendanceItem.entry?.funding?.headStartVisible ?? false
            const sfpVisible = attendanceItem.entry?.funding?.sfpVisible ?? false

            return (
              <Paper
                key={attendanceItem.contact.id}
                p='lg'
                pos='relative'
              >
                <LoadingOverlay
                  visible={isBusy}
                  radius='lg'
                />
                {rowErrors[attendanceItem.enrollment.id]?.length ? (
                  <Alert
                    color='red'
                    icon={<IconAlertCircle size={18} />}
                    mb='xs'
                    title='This student was not saved'
                  >
                    <Stack spacing={2}>
                      {map(rowErrors[attendanceItem.enrollment.id], (message, index) => (
                        <Text
                          key={index}
                          size='sm'
                        >
                          {message}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                ) : null}
                <Grid gutter='xs'>
                  <Grid.Col sm={12}>
                    <Title
                      fw={700}
                      order={4}
                    >
                      {attendanceItem.contact.name}
                    </Title>
                  </Grid.Col>
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <SelectInput
                      data={options?.participationTypes ?? []}
                      label='Participation Type'
                      mb={0}
                      readOnly
                      tabIndex={-1}
                      value={attendanceItem.enrollment.participationType}
                      variant='unstyled'
                    />
                  </Grid.Col>
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <Input.Wrapper
                      label='DPP Scholarship'
                      labelElement='div'
                    >
                      <Group spacing='xs'>
                        <Text size='sm'>{attendanceItem.enrollment.gapScholarshipStatus ?? 'N/A'}</Text>
                        <ScholarshipSubStatusBadge enrollment={attendanceItem.enrollment} />
                      </Group>
                    </Input.Wrapper>
                  </Grid.Col>
                  {map(
                    [
                      ['Tuition Credit', currencyOrDash(attendanceItem.enrollment.tuitionCredit)],
                      ['DPP Scholarship Award', currencyOrDash(attendanceItem.enrollment.gapAwardAmount)],
                      ['Student Tuition Cap', currencyOrDash(attendanceItem.enrollment.tuitionCap)],
                      ['Scholarship Cap', currencyOrDash(attendanceItem.enrollment.dppTuitionCap)]
                    ],
                    ([label, value]) => (
                      <Grid.Col
                        key={label}
                        xs={6}
                        md={2}
                      >
                        <Input.Wrapper
                          label={label}
                          labelElement='div'
                        >
                          <Text size='sm'>{value}</Text>
                        </Input.Wrapper>
                      </Grid.Col>
                    )
                  )}
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <NumberInput
                      disabled={isBusy}
                      formatter={numberInputFormatter}
                      label='Days Scheduled'
                      mb={0}
                      min={0}
                      readOnly={isLocked}
                      required={!isLocked}
                      variant={variant}
                      {...form.getInputProps(`entries.${attendanceItemIdx}.daysScheduled`)}
                      onChange={(value) =>
                        form.setFieldValue(
                          `entries.${attendanceItemIdx}.daysScheduled`,
                          isNumber(value) && value >= 0 ? value : 0
                        )
                      }
                    />
                  </Grid.Col>
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <NumberInput
                      disabled={isBusy}
                      formatter={numberInputFormatter}
                      label='Days Attended'
                      mb={0}
                      min={0}
                      readOnly={isLocked}
                      required={!isLocked}
                      variant={variant}
                      {...form.getInputProps(`entries.${attendanceItemIdx}.daysAttended`)}
                      onChange={(value) =>
                        form.setFieldValue(
                          `entries.${attendanceItemIdx}.daysAttended`,
                          isNumber(value) && value >= 0 ? value : 0
                        )
                      }
                    />
                  </Grid.Col>
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <DateInput
                      clearable
                      disabled={isBusy}
                      label='Late Start Date'
                      mb={0}
                      maxDate={form.values.entries[attendanceItemIdx]?.lateStartMax ?? undefined}
                      minDate={form.values.entries[attendanceItemIdx]?.lateStartMin ?? undefined}
                      placeholder='N/A'
                      readOnly={isLocked}
                      variant={variant}
                      {...form.getInputProps(`entries.${attendanceItemIdx}.lateStartDate`)}
                    />
                  </Grid.Col>
                  <Grid.Col
                    xs={6}
                    md={2}
                  >
                    <DateInput
                      clearable
                      disabled={isBusy}
                      label='Early End Date'
                      mb={0}
                      maxDate={form.values.entries[attendanceItemIdx]?.earlyEndMax ?? undefined}
                      minDate={form.values.entries[attendanceItemIdx]?.earlyEndMin ?? undefined}
                      placeholder='N/A'
                      readOnly={isLocked}
                      variant={variant}
                      {...form.getInputProps(`entries.${attendanceItemIdx}.earlyEndDate`)}
                    />
                  </Grid.Col>
                  {/* Both duplicate the new Other Funding / UPK Type controls, so they are
                  retired wherever the funding block renders. The gate is inverted rather than
                  dropped: a pre-2026/27 program year, or a backend without PR #730 deployed,
                  renders no funding block at all, and these two are the only way a provider can
                  change either value on the sheets they submit against today. */}
                  {!row?.fundingCollected && (
                    <Grid.Col
                      xs={6}
                      lg={4}
                    >
                      <SelectInput
                        clearable
                        data={[
                          {
                            label: 'Yes',
                            value: 'yes'
                          },
                          {
                            label: 'No',
                            value: 'no'
                          }
                        ]}
                        disabled={isBusy}
                        label='Received Other Non-UPK Tuition Support'
                        mb={0}
                        placeholder='N/A'
                        readOnly={isLocked}
                        required={attendanceItem.enrollment.gapScholarshipStatus === 'Yes'}
                        variant={variant}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.rcvdOtherTuitionSupport`)}
                      />
                    </Grid.Col>
                  )}
                  {!row?.fundingCollected && (
                    <Grid.Col
                      xs={6}
                      lg={4}
                    >
                      <SelectInput
                        clearable
                        data={[
                          {
                            label: 'Yes',
                            value: 'yes'
                          },
                          {
                            label: 'No',
                            value: 'no'
                          }
                        ]}
                        disabled={isBusy}
                        label='UPK Participant'
                        mb={0}
                        placeholder='N/A'
                        readOnly={isLocked}
                        required={attendanceItem.enrollment.gapScholarshipStatus === 'Yes'}
                        variant={variant}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.upkParticipant`)}
                      />
                    </Grid.Col>
                  )}
                  {row?.fundingCollected && (
                    <Grid.Col
                      xs={6}
                      md={3}
                    >
                      <SelectInput
                        aria-describedby={helpIds.ccap}
                        aria-label='CCCAP'
                        aria-required={!isLocked && !scholarshipLocked}
                        clearable
                        data={YES_NO_OPTIONS}
                        inputWrapperOrder={BELOW_CONTROL}
                        disabled={isBusy}
                        label={fundingLabel('CCCAP', CCCAP_HELP_LABEL)}
                        mb={0}
                        readOnly={isLocked || scholarshipLocked}
                        title={
                          scholarshipLocked ? scholarshipDisabledLabel(attendanceItem.enrollment.gapStatus) : undefined
                        }
                        variant={variant}
                        // MIQ-2923: the roster's lock note, visible rather than only on
                        // hover. It replaces the pre-fill hint and highlight on a locked
                        // box, which ask for a review the provider cannot act on. Same
                        // for Head Start below.
                        {...(scholarshipLocked
                          ? { description: scholarshipLockedNote(attendanceItem.enrollment.gapStatus) }
                          : prefillProps(row, 'ccapEnrolled'))}
                        {...fundingPlaceholderProps(!isLocked && !scholarshipLocked, row.ccapEnrolled)}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.ccapEnrolled`, { withError: false })}
                        onChange={clearAndSet(attendanceItemIdx, 'ccapEnrolled')}
                      />
                    </Grid.Col>
                  )}
                  {row?.fundingCollected && headStartVisible && (
                    <Grid.Col
                      xs={6}
                      md={3}
                    >
                      <SelectInput
                        aria-describedby={helpIds.headStart}
                        aria-label='Head Start'
                        aria-required={!isLocked && !scholarshipLocked}
                        clearable
                        data={YES_NO_OPTIONS}
                        inputWrapperOrder={BELOW_CONTROL}
                        disabled={isBusy}
                        label={fundingLabel('Head Start', HEAD_START_HELP_LABEL)}
                        mb={0}
                        readOnly={isLocked || scholarshipLocked}
                        title={
                          scholarshipLocked ? scholarshipDisabledLabel(attendanceItem.enrollment.gapStatus) : undefined
                        }
                        variant={variant}
                        {...(scholarshipLocked
                          ? { description: scholarshipLockedNote(attendanceItem.enrollment.gapStatus) }
                          : prefillProps(row, 'headStart'))}
                        {...fundingPlaceholderProps(!isLocked && !scholarshipLocked, row.headStart)}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.headStart`, { withError: false })}
                        onChange={clearAndSet(attendanceItemIdx, 'headStart')}
                      />
                    </Grid.Col>
                  )}
                  {row?.fundingCollected && (
                    <Grid.Col
                      xs={6}
                      md={3}
                    >
                      <SelectInput
                        aria-describedby={helpIds.upk}
                        aria-label='UPK Type'
                        aria-required={!isLocked}
                        clearable
                        data={UPK_OPTIONS}
                        inputWrapperOrder={BELOW_CONTROL}
                        disabled={isBusy}
                        label={fundingLabel('UPK Type', UPK_HELP_LABEL)}
                        mb={0}
                        readOnly={isLocked}
                        variant={variant}
                        {...prefillProps(row, 'upkType')}
                        {...fundingPlaceholderProps(!isLocked, row.upkType)}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.upkType`, { withError: false })}
                        onChange={clearAndSet(attendanceItemIdx, 'upkType')}
                      />
                    </Grid.Col>
                  )}
                  {row?.fundingCollected && sfpVisible && (
                    <Grid.Col
                      xs={6}
                      md={3}
                    >
                      <SelectInput
                        aria-describedby={helpIds.sfp}
                        aria-label='SFP'
                        aria-required={!isLocked}
                        clearable
                        data={SFP_OPTIONS}
                        inputWrapperOrder={BELOW_CONTROL}
                        disabled={isBusy}
                        label={fundingLabel('SFP', SFP_HELP_LABEL)}
                        mb={0}
                        readOnly={isLocked}
                        variant={variant}
                        {...prefillProps(row, 'sfpEnrolled')}
                        {...fundingPlaceholderProps(!isLocked, row.sfpEnrolled)}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.sfpEnrolled`, { withError: false })}
                        onChange={clearAndSet(attendanceItemIdx, 'sfpEnrolled')}
                      />
                    </Grid.Col>
                  )}
                  {row?.fundingCollected &&
                    currencyFundingFields.map(({ field, help, helpId, label, optional }) => {
                      const required = !optional && !isLocked
                      // MIQ-2921: read live from the GET, never seeded into the form row,
                      // so it survives the save echo without a reseed and vanishes on a
                      // backend without MIQ-2920. Hidden on a locked sheet for the same
                      // reason the pre-fill highlight is: it asks for an action nobody
                      // can take there, and placeholder data during a period switch
                      // must not offer the outgoing period's amount to the incoming one.
                      const priorCap =
                        field === 'requestedCap' && !isLocked ? priorRequestedCap(attendanceItem.entry?.funding) : null
                      // The source can be older than last month (the server skips periods
                      // with no qualifying entry), so the control names the period the
                      // amount came from, formatted like the pre-fill hint beside it.
                      // "last month" only if the name is ever missing.
                      const priorCapPeriod =
                        prefillPeriodName(attendanceItem.entry?.funding?.priorRequestedCapPeriod ?? null) ??
                        'last month'

                      return (
                        <Grid.Col
                          key={field}
                          xs={6}
                          md={3}
                        >
                          <NumberInput
                            aria-describedby={helpId}
                            aria-label={label}
                            aria-required={required}
                            // The description slot sits under the box (BELOW_CONTROL) and
                            // is free for this field: requestedCap is never pre-filled.
                            // The click goes through the same setter as typing, so the
                            // POST and the MIQ-2670 review flag behave exactly as today.
                            description={
                              priorCap !== null && (
                                <Anchor
                                  component='button'
                                  disabled={isBusy}
                                  onClick={() => form.setFieldValue(`entries.${attendanceItemIdx}.${field}`, priorCap)}
                                  size='xs'
                                  // Underlined at rest, not only on hover: in the description
                                  // slot it otherwise reads as one more line of hint text.
                                  sx={{ textDecoration: 'underline' }}
                                  type='button'
                                >
                                  Same as {priorCapPeriod}: {formatCurrency(priorCap)}
                                </Anchor>
                              )
                            }
                            inputWrapperOrder={BELOW_CONTROL}
                            disabled={isBusy}
                            hideControls
                            icon='$'
                            label={fundingLabel(label, help, optional)}
                            mb={0}
                            min={0}
                            precision={2}
                            readOnly={isLocked}
                            variant={variant}
                            {...prefillProps(row, field)}
                            {...fundingPlaceholderProps(required, row[field])}
                            {...form.getInputProps(`entries.${attendanceItemIdx}.${field}`, { withError: false })}
                            // Only a value's own error shows here (a $0 Published Tuition
                            // Rate). A blank box's "Required" stays the red placeholder
                            // above rather than an error state -- see
                            // fundingPlaceholderProps.
                            error={row[field] === '' ? undefined : form.errors[`entries.${attendanceItemIdx}.${field}`]}
                            onChange={(value) => {
                              clearPrefill(attendanceItemIdx, field)
                              form.setFieldValue(`entries.${attendanceItemIdx}.${field}`, value)
                            }}
                          />
                        </Grid.Col>
                      )
                    })}
                  {/* Every row of a pre-2026/27 sheet, as it always was; on a funding
                      sheet only where the answer is owed (MIQ-2954). Live status, not
                      the row copy -- see liveMetaByEnrollmentId. */}
                  {(!row?.fundingCollected || payingZeroRequired(attendanceItem.enrollment.gapStatus)) && (
                    <Grid.Col
                      xs={6}
                      lg={4}
                    >
                      <SelectInput
                        clearable
                        data={[
                          {
                            label: 'Yes',
                            value: 'yes'
                          },
                          {
                            label: 'No',
                            value: 'no'
                          }
                        ]}
                        disabled={isBusy}
                        label='DPP Family Paying $0'
                        mb={0}
                        placeholder='N/A'
                        readOnly={isLocked}
                        required={!isLocked && payingZeroRequired(attendanceItem.enrollment.gapStatus)}
                        variant={variant}
                        {...form.getInputProps(`entries.${attendanceItemIdx}.isDppFamilyPayingZero`)}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              </Paper>
            )
          })}
        </>
      ) : null}
      {!isLocked && (
        <Group
          align='center'
          position='right'
        >
          <Button
            color='brand-red'
            disabled={!isDirty || !isValid || isBusy}
            leftIcon={<IconChecks size={18} />}
            onClick={async () => {
              if (siteId && programYearId && periodId) {
                const { entries, ...values } = form.values
                // Cleared before the request, not after: a thrown request (network failure,
                // 500) must not leave the previous submit's per-row alerts standing beside a
                // toast about a different failure.
                setRowErrors({})
                const response = await updateAttendance({
                  ...values,
                  // Built key by key rather than spread: the row also carries the form's own
                  // helpers (the date bounds, maximumAllowedDays, the funding visibility flags,
                  // the prefill metadata), none of which belong on the wire.
                  entries: map(entries, (row) => {
                    const live = liveMetaByEnrollmentId.get(row.enrollmentId ?? '')
                    const entry: SugarAttendanceUpsertEntry['entry'] = {
                      daysAttended: row.daysAttended ? Math.max(row.daysAttended, 0) : 0,
                      daysScheduled: row.daysScheduled ? Math.max(row.daysScheduled, 0) : 0,
                      description: row.description,
                      earlyEndDate: row.earlyEndDate ? dayjs(row.earlyEndDate).format('YYYY-MM-DD') : null,
                      lateStartDate: row.lateStartDate ? dayjs(row.lateStartDate).format('YYYY-MM-DD') : null,
                      // Echoed unchanged: both controls were retired in MIQ-2345 but their
                      // stored values must survive, so they are seeded from the GET and posted
                      // straight back.
                      receivedOtherTuitionSupport: row.rcvdOtherTuitionSupport,
                      upkParticipant: row.upkParticipant
                    }

                    // Posted only where the control is shown. On a funding sheet a Pending
                    // row's key is left off the wire entirely -- not sent as null, which the
                    // server would store as a cleared answer -- so an answer saved while the
                    // row was still asked is left alone (MIQ-2954).
                    if (!row.fundingCollected || payingZeroRequired(live?.gapStatus ?? null)) {
                      entry.isDppFamilyPayingZero = row.isDppFamilyPayingZero
                    }

                    if (row.fundingCollected) {
                      entry.funding = toFundingWrite(row, {
                        headStart: live?.headStartVisible ?? row.headStartVisible,
                        sfp: live?.sfpVisible ?? row.sfpVisible
                      })
                    }

                    return { enrollmentId: row.enrollmentId, entry }
                  }),
                  periodId,
                  programYearId,
                  siteId
                })

                showMessagesNotifications(response.messages)

                // The selection can move while a save is in flight. The in-table pickers
                // are disabled during one, but the header's site picker is not: it only
                // prompts on unsaved changes, and a save in flight always has them, so a
                // provider who confirms that prompt lands here with the form already
                // reseeded for the new site. Whoever owns the selection now owns the form
                // and its per-row errors; this callback no longer does. Both the rejection
                // map and the reseed are gated on it — rejections are keyed by
                // enrollmentId, which a same-site period switch carries over, so posting
                // them blind would pin the old month's errors to the new month's rows.
                const { current } = selectionRef
                const isStillCurrent =
                  current.periodId === periodId && current.programYearId === programYearId && current.siteId === siteId

                // A refused row arrives as HTTP 200, success: true and one type: 'error'
                // message carrying its enrollmentId, while every other row saves. Replacing
                // the whole map also clears the previous submit's rejections when this one
                // carries none.
                const rejections = filter(
                  response.messages,
                  (message) => message.type === 'error' && Boolean(message.enrollmentId)
                )

                if (isStillCurrent) {
                  setRowErrors(mapValues(groupBy(rejections, 'enrollmentId'), (messages) => map(messages, 'message')))
                }

                if (response.success && isStillCurrent) {
                  // A rejected response's payload is not the saved sheet (it must never be
                  // cached as one), so reseed the form from the sheet the awaited invalidation
                  // in useUpdateAttendance already refetched, rather than the rejected envelope.
                  const saved = isAttendanceSaveRejected(response)
                    ? queryClient.getQueryData<SugarAttendanceResponse>(
                        attendanceQueryKey({ periodId, programYearId, siteId })
                      )?.data
                    : response.data

                  resetForm(saved)

                  // partialProcessing can come back success: true, carrying per-item
                  // errors AND sheet.locked: true -- the contract's own example does
                  // exactly that. Replaying rejected values into a sheet that came back
                  // locked or gated would dirty a form whose Submit is hidden, leaving
                  // the provider an unsaved-changes warning for values they have no way
                  // to save. Keep the row error; skip the replay.
                  const isSheetEditable =
                    Boolean(saved) && saved?.sheet.locked !== true && saved?.sheet.attendanceGated !== true

                  // A rejected item was not saved, so the reset above just replaced what the
                  // provider typed with the server's untouched copy. Put their values back and
                  // leave the form dirty, so Submit is live for the retry — this is also the
                  // create-path case, where the server has made the entry with no days and the
                  // corrected re-submit succeeds. Joined on enrollmentId rather than array
                  // index: the reseeded rows come from the server's ordering, not ours. The
                  // updater must stay functional: resetForm's own setValues above is still
                  // queued at this point, so reading `current` is what guarantees this callback
                  // sees the post-reseed state. A plain object payload built from the pre-submit
                  // `entries` would silently clobber every successfully-saved row with its stale
                  // value instead.
                  const rejectedIds = new Set(map(rejections, 'enrollmentId'))
                  // Post-save truth, straight off the sheet reseeded above.
                  const lockedAfterSave = new Set(
                    map(
                      filter(saved?.entries ?? [], ({ enrollment }) =>
                        isScholarshipLocked(enrollment?.gapStatus ?? null)
                      ),
                      ({ enrollment }) => enrollment?.id
                    )
                  )

                  if (rejectedIds.size && isSheetEditable) {
                    form.setValues((current) => ({
                      ...current,
                      entries: map(current.entries, (savedRow) => {
                        if (!savedRow.enrollmentId || !rejectedIds.has(savedRow.enrollmentId)) return savedRow

                        const typed = find(entries, ['enrollmentId', savedRow.enrollmentId])

                        if (!typed) return savedRow

                        // Replay only what the provider can still act on. A row rejected
                        // *because* its scholarship was confirmed mid-submit comes back with
                        // CCCAP and Head Start locked: restoring the refused values there
                        // would leave read-only controls holding the very values the server
                        // refused, reposted on every retry, with a row error the provider has
                        // no way to clear. Those two keep the server's copy, which the write
                        // rule diffs to a no-op.
                        //
                        // The lock is read from `saved` -- the sheet this submit just
                        // reseeded from -- and NOT from liveMetaByEnrollmentId, which is
                        // derived from the query cache and still describes the render before
                        // this submit. On the one submission that newly locks a row, that memo
                        // reports it unlocked, which is exactly the case this guard exists for.
                        return lockedAfterSave.has(savedRow.enrollmentId)
                          ? { ...typed, ccapEnrolled: savedRow.ccapEnrolled, headStart: savedRow.headStart }
                          : typed
                      })
                    }))
                  }
                }
              }
            }}
            size='lg'
            variant='filled'
          >
            Submit
          </Button>
        </Group>
      )}
      <ConfirmModal
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
        opened={isBlockingNavigation}
      >
        <Text size='sm'>You have unsaved changes, are you sure you want to leave?</Text>
      </ConfirmModal>
    </Stack>
  )
}

AttendanceTable.displayName = 'AttendanceTable'
